// Search filter validation — single source of truth for constants, sanitizers,
// and validators shared across load-hub and agentic-hub.

// ── Constants ────────────────────────────────────────────────────────────────

export const SEARCH_FILTER_LIMITS = {
    DHD_DEFAULT: 150,
    DHD_MAX: 450,
    MAX_RPM: 100,
    MAX_RATE: 100_000,
    MAX_MILES: 99_999,
    MAX_WEIGHT_LBS: 99_999,
    MAX_LENGTH_FT: 199,
    TEXT_MAX_LENGTH: 200,
    SEARCH_QUERY_MAX_LENGTH: 200,
} as const

// ── Sanitizers ───────────────────────────────────────────────────────────────

interface NumericSanitizeOptions {
    min?: number
    max?: number
    allowEmpty?: boolean
    allowDecimal?: boolean
}

export const sanitizeNumericInput = (raw: string, opts: NumericSanitizeOptions = {}): string | null => {
    const { min = 0, max, allowEmpty = true, allowDecimal = false } = opts
    if (!(allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/).test(raw)) return null
    // A lone "." is an incomplete/empty value: keep it so the user can type "2." on the
    // way to "2.5", but honor allowEmpty just like the empty-string case below.
    if (allowDecimal && raw === '.') return allowEmpty ? raw : null
    if (!raw) return allowEmpty ? '' : null
    const num = allowDecimal ? parseFloat(raw) : parseInt(raw, 10)
    if (Number.isNaN(num)) return allowEmpty ? '' : null
    if (num < min) return min.toString()
    if (max !== undefined && num > max) return max.toString()
    return raw
}

export const sanitizeDeadhead = (value: string): string => {
    if (!value) return SEARCH_FILTER_LIMITS.DHD_DEFAULT.toString()
    const num = parseFloat(value)
    if (Number.isNaN(num)) return ''
    if (num < 0) return '0'
    if (num > SEARCH_FILTER_LIMITS.DHD_MAX) return SEARCH_FILTER_LIMITS.DHD_MAX.toString()
    return value
}

export const trimSearchText = (value: string, maxLength: number = SEARCH_FILTER_LIMITS.TEXT_MAX_LENGTH): string => {
    return value.slice(0, maxLength)
}

export const safeParseNumber = (value: string, max?: number): number | undefined => {
    if (!value.trim()) return undefined
    const n = Number(value)
    if (!Number.isFinite(n) || n < 0) return undefined
    if (max !== undefined && n > max) return max
    return n
}

// ── Validators ───────────────────────────────────────────────────────────────

export const validateLocation = (city?: string, state?: string): boolean => {
    return Boolean(city?.trim() && state?.trim())
}

export const validateDateRange = (startDate: Date | null, endDate: Date | null): boolean => {
    if (!startDate || !endDate) return false
    return startDate <= endDate
}

export const validateEquipment = (equipment: string[]): boolean => {
    return Array.isArray(equipment) && equipment.length > 0
}

export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

export const validateSearchRequest = (
    origin: Record<string, unknown> | null,
    startDate: Date | null,
    endDate: Date | null,
    equipmentTypes: string[]
): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    if (!origin || !origin.city || !origin.stateProv) {
        errors.push('Origin is required')
    }

    if (!validateDateRange(startDate, endDate)) {
        errors.push('Valid date range is required')
    }

    if (!validateEquipment(equipmentTypes)) {
        errors.push('At least one equipment type is required')
    }

    return {
        valid: errors.length === 0,
        errors,
    }
}
